// import Dialog from "~/components/molecules/Dialog"
import { inject, Observer } from "mobx-react"
import { WithRouterProps } from "next/dist/client/with-router"
import Head from "next/head"
import { withRouter } from "next/router"
import React from "react"

import CourseApplyDoneModal from "~/components/organisms/CourseApplyDoneModal"
import CourseInputPersonalDataModal from "~/components/organisms/CourseInputPersonalDataModal"
import CourseSchduleSelectModal from "~/components/organisms/CourseSchduleSelectModal"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import Background from "~/components/organisms/course-detail/Background"
import Career from "~/components/organisms/course-detail/Career"
import Consultation from "~/components/organisms/course-detail/Consultation"
import Curriculum from "~/components/organisms/course-detail/Curriculum"
import Feature from "~/components/organisms/course-detail/Feature"
import GraduatesVoice from "~/components/organisms/course-detail/GraduatesVoice"
import MainVisual from "~/components/organisms/course-detail/MainVisual"
import Price from "~/components/organisms/course-detail/Price"
import PromoteArea from "~/components/organisms/course-detail/PromoteArea"
import Teacher from "~/components/organisms/course-detail/Teacher"
import { Course } from "~/entities/Course"
import { TmpUserInfo } from "~/entities/User"
import { staticPath } from "~/services/helper"
import ApplicationStore from "~/stores/ApplicationStore"
import RootStore from "~/stores/RootStore"
import UiStore from "~/stores/UiStore"
import UserStore from "~/stores/UserStore"

interface Props {
  applicationStore: ApplicationStore
  course: Course
  uiStore: UiStore
  userStore: UserStore
}

interface State {
  selectedScheduleValues: string[]
  isLoading: boolean
  tmpUserInfo: TmpUserInfo
}

@inject("applicationStore", "uiStore", "userStore")
class DataScienceCourse extends React.Component<WithRouterProps & Props, State> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { courseStore } = initialStoreData
    const course = await courseStore.fetchById(4)
    return { course }
  }

  state = {
    selectedScheduleValues: [],
    isLoading: false,
    tmpUserInfo: {
      name: null,
      email: null,
    },
  }

  constructor(props: WithRouterProps & Props) {
    super(props)
    const { uiStore } = props
    switch (props.router.query.openModal) {
      case "selectSchedule":
        uiStore.toggleCourseSchduleSelectModal()
        break
      case "confirmApply":
        uiStore.toggleConfirmCourseApplyModal()
        break
      case "applyComplete":
        uiStore.toggleCompleteApplyCourseModal()
        break
    }
  }

  render() {
    const { applicationStore, uiStore, userStore, course, router } = this.props
    const { selectedScheduleValues, isLoading, tmpUserInfo } = this.state
    const handleSelectSchedule = (schedules: string[]) => {
      this.setState({
        selectedScheduleValues: schedules,
      })
    }

    const toggleLoading = () => {
      this.setState({
        isLoading: !this.state.isLoading,
      })
    }

    const updateTmpUserInfo = (values: TmpUserInfo) => {
      this.setState({
        tmpUserInfo: values,
      })
    }

    const mainActionForSchduleSelectModal = async () => {
      const { utm_source, utm_medium } = router.query
      const channel = utm_source && utm_medium === "alliance" ? String(utm_source) : undefined

      toggleLoading()
      selectedScheduleValues.sort()
      if (userStore.authed) {
        await applicationStore.courseConsultationApply(4, selectedScheduleValues.sort(), channel)
      } else {
        await applicationStore.courseConsultationApplyByGuest(4, tmpUserInfo, selectedScheduleValues.sort(), channel)
      }
      uiStore.toggleCourseSchduleSelectModal()
      uiStore.toggleCompleteApplyCourseModal()
      toggleLoading()
    }

    return (
      <>
        <Head>
          <title>データサイエンス講座 | Infra（インフラ）</title>
          <meta name="description" content={course.description} />
          <meta name="og:title" content="データサイエンス講座 | Infra（インフラ）" />
          <meta
            name="og:description"
            content="Pythonを用いながら、統計学や機械学習、さらには深層学習までを体系的に学び、初心者からデータサイエンス職へのエントリーレベルを目指します。"
          />
          <meta
            name="og:image"
            content={process.env.APP_HOST + staticPath("/static/images/course/data-science/ogp.png")}
          />
        </Head>
        <Header showTab logoType="Courses" />
        <MainVisual
          mSrcPc={staticPath("/static/images/course/data-science/mv.png")}
          mSrcSp={staticPath("/static/images/course/data-science/mv-sp.png")}
          typoPath={staticPath("/static/images/course/data-science/typo.svg")}
          heading={`データサイエンス講座`}
          subHeading={`未経験・初心者から\n企業に求められるデータサイエンティストへ！`}
          description=""
          courseLineUrl={course.lineUrl}
          announced
        />
        <Background
          message={`ChatGPTを始めとしたAI技術の急速な発達に加え、より多くのものがインターネットに接続されて企業が莫大な個人の行動データなどを取得できるようになった昨今、いかに効率的に効果的なデータを収集し、収集したデータをどのようにビジネスの課題解決に役立てていくかが求められています。
            本講座では、文系学部やプログラミング未経験者の方でもデータサイエンティストとしての一歩を踏み出せるよう、現場で働くプロからからPythonをデータサイエンティストに必要な知識をゼロから体系的に学びます。
          `}
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "データサイエンス職の需要が年々高騰",
              description:
                "2017年4月時点の国内求人数は1960件だったのに対し、翌年同月で1.6倍の3136件に増えている。2020年4月時点では6141件と3年で求人数が3倍に。",
              linkURL: "https://www.atpress.ne.jp/news/157665",
              linkText: "求人検索エンジン「スタンバイ」調べ",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "データサイエンティスト 企業の6割「採用目標届かず」",
              description:
                "過去1年間でデータサイエンティストを採用しようとした企業に目標としていた人数を確保できたかどうかを聞くと「確保できなかった」「どちらかといえば確保できなかった」との回答が合計で58%にのぼった。「確保できた」との回答は17%にとどまった。",
              linkURL: "https://www.nikkei.com/article/DGXMZO58568540Y0A420C2000000/",
              linkText: "日本経済新聞より",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-3-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-3-sp.svg"),
              heading: "データサイエンティストに必要なスキル",
              description:
                "実際にデータサイエンティストとして活躍するには、RやPythonといったプログラミング言語や、数学・統計学の知識を深める必要がある。これまでのデータ分析の仕事は、分析・レポーティング業務などで終わっていたが、今はビジネス課題を解決するための予測モデルを作る所まで求められるようになっている。",
              linkURL: "https://www.itmedia.co.jp/news/articles/1811/12/news015.html",
              linkText: "ITmedia NEWSより",
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Career
          courseName="データサイエンス講座"
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "企業は統計や機械学習の経験を評価する",
              description: "講座では機械学習の基礎知識から実際のビジネスデータを用いた実践までをカバー",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "実務スキルを身につけ就職を一歩リード",
              description: "講座終了後に長期インターンで実務経験を積んでから就職するのがオススメ",
            },
          ]}
        />
        <GraduatesVoice />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Feature
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "実際のビジネス課題に取り組む",
              description:
                "Pythonや統計、機械学習の習得はもちろん、リアルなビジネスデータを用いてビジネス課題へ取り組む実践の場を提供。",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "講師との1対1の面談で徹底サポート",
              description:
                "ビジネスの現場で活躍するデータサイエンティストが講師。1対1の面談を通してスキルが身につくよう徹底サポート。",
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Teacher
          items={[
            {
              img: staticPath("/static/images/course/data-science/teacher.png"),
              name: "石井 良平",
              position: "XTX株式会社 代表取締役社長",
              introduction:
                "これまでデータサイエンス系企業、社会人向けのデータサイエンススクール講師、医科大助教、医学部非常勤講師などに従事してきました。データサイエンスの実務・教育・数理、全てが守備範囲です。本講座を通して、レガシーな統計解析も、機械学習によるモデリングも、強力だけど時にブラックボックスな深層学習も、それぞれ楽しさがあると伝えられたら幸いです。共著書『データ分析実務スキル検定の教科書』（インプレス）",
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Curriculum
          heading="週1回のオンライン講義＋講師との個別面談"
          period="第8期 2023年8月24日（木）より開講予定"
          curriculumItems={[
            {
              periodImgPc: staticPath("/static/images/course/period-2month-pc.svg"),
              periodImgSp: staticPath("/static/images/course/period-2month-sp.svg"),
              heading: "全9回",
              date: "",
              caption: "1. イントロダクション・Python入門",
              body: "データサイエンス（データサイエンティスト）についての最近の動向や仕事の種類・必要スキルなどを簡単に紹介するとともに、データ分析におけるPythonの使い方を解説します。",
            },
            {
              caption: "2. Pythonによる統計学",
              body: "統計学の内容をPythonで実現するとともに、実践的なデータで統計学的な解析を行います。",
            },
            {
              caption: "3. 機械学習入門",
              body: "機械学習のイントロダクションとして、教師なし学習と教師あり学習の違いを理解すること、教師あり学習についていくつかのモデルの概観を理解することを目的にします。",
            },
            {
              caption: "4〜6. 機械学習 教師あり学習",
              body: "教師あり学習のうち、数値予測モデル・分類モデルを扱った後、予測モデル作成コンペであるKaggleを通して、予測モデル作成の一連のフローに実践的に入門します。",
            },
            {
              caption: "7. 機械学習 教師なし学習",
              body: "教師なし学習の代表的な手法としてクラスター分析と主成分分析（次元圧縮）を扱います。",
            },
            {
              caption: "8. 機械学習の先端と深層学習",
              body: "現代AIを支える深層学習という分野の基礎的なモデルについて解析を行います。",
            },
            {
              caption: "9. 発表会＋まとめ",
              body: "事前に配布されたビジネスデータの解析結果について各自簡単に発表していただきます。その後、これまでの講座のまとめと総復習を行い、講座受講後の学び方についてもガイダンスを行います。",
            },
          ]}
          recommendItems={[
            "データサイエンティストとしてインターン/就職したい方",
            "Pythonを使ったデータ処理や機械学習に興味のある方",
            "AI時代に備えてデータサイエンスの知識を身につけ、ビジネスに活用できるようになりたい方",
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Price
          items={[
            {
              price: 217800,
              splitNum: 12,
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Consultation job="データサイエンティスト" />
        <PromoteArea
          bgPath={staticPath("/static/images/course/data-science/mv.png")}
          courseName="データサイエンス講座"
          courseLineUrl={course.lineUrl}
          announced
        />
        <Observer>
          {() => (
            <>
              {/* <Dialog
                open={uiStore.authPromptModalOpen}
                heading="登録またはログインが必要です"
                mainAction={{
                  label: "登録する",
                  action: () =>
                    window.open(uiStore.redirectUrlAfterAuth, "_self"),
                }}
                subAction={{
                  label: "キャンセル",
                  action: () => uiStore.toggleAuthPromptModal(),
                }}
              >
                エントリー/お気に入りを利用するためには、新規登録またはログインが必要です。
              </Dialog> */}
              {/* <Dialog
                open={uiStore.confirmCourseApplyModalOpen}
                heading="講座申し込みの確認"
                mainAction={{
                  label: "申し込む",
                  action: async () => {
                    toggleLoading()
                    uiStore.toggleConfirmCourseApplyModal()
                    uiStore.toggleCompleteApplyCourseModal()
                    // 広告CV設定のため、敢えてリロードさせる
                    location.href = `${
                      process.env.APP_HOST + location.pathname
                    }?openModal=applyComplete`
                    toggleLoading()
                  },
                  className: "gtm_course-complete-application",
                  isLoading: isLoading,
                }}
                subAction={{
                  label: "キャンセル",
                  action: () => uiStore.toggleConfirmCourseApplyModal(),
                }}
              >
                こちらの講座は<PaidAlert>有料</PaidAlert>となります。
                <br />
                申し込みを確定してよろしいですか？
              </Dialog> */}
              <CourseInputPersonalDataModal
                open={uiStore.authPromptModalOpen}
                close={() => uiStore.toggleAuthPromptModal()}
                updateTmpUserInfo={updateTmpUserInfo}
                mainAction={() => {
                  uiStore.toggleAuthPromptModal()
                  uiStore.toggleCourseSchduleSelectModal()
                }}
              />
              <CourseSchduleSelectModal
                open={uiStore.courseSchduleSelectModalOpen}
                selectedScheduleValues={selectedScheduleValues}
                onSelectSchedule={handleSelectSchedule}
                mainAction={mainActionForSchduleSelectModal}
                subAction={() => uiStore.toggleCourseSchduleSelectModal()}
                isLoading={isLoading}
              />
              <CourseApplyDoneModal
                open={uiStore.completeApplyCourseModalOpen}
                close={() => uiStore.toggleCompleteApplyCourseModal()}
                addFriend={() => window.open(course.lineUrl)}
                lineQrImageUrl={course.lineQrImageUrl}
              />
            </>
          )}
        </Observer>
        <Footer />
      </>
    )
  }
}

export default withRouter(DataScienceCourse)
